import type { Environment, SgAuthAdapterMap } from '@sg/common';
import type { SgAppBranding } from '@sg/pre-app-init';
import { BehaviorSubject } from 'rxjs';
import { SgApplicationConfiguration } from './app-configurations/types';
import { getBrandingFromUrl, getDeployUrl } from './utils';

const noop = () => {};

function applyBrandingFromMap(
  branding: SgAppConfig['branding'],
  brandName: string,
  brandingMap: SgApplicationConfiguration['brandingMap']
) {
  if (brandName in brandingMap) {
    const brandingConfig = brandingMap[brandName];
    if (brandingConfig.redirectToBranding && brandingMap[brandingConfig.redirectToBranding]) {
      Object.assign(branding, brandingMap[brandingConfig.redirectToBranding]);
      branding.name = brandingConfig.redirectToBranding;
    } else {
      Object.assign(branding, brandingConfig);
      branding.name = brandName;
    }
    return true;
  }
  return false;
}

export class SgAppConfig {
  static DEFAULT_BRAND: SgAppConfig['branding'] = {
    redirectToBranding: null,
    name: 'sharegain',
    css: 'sg-default-theme',
    theme: 'light',
    logo: 'assets/images/Logo_Horizontal_BlueGradient.svg',
    logoContrast: 'assets/images/white-sharegain-logo.svg',
    preLogo: 'assets/images/sharegain-logo-horizontal-black-on-white.svg',
    backgroundImg: '',
    sidenavBackgroundImg: '',
    hideSideNav: false,
    showSideBarExpender: true,
    showBurgerMenu: true,
    sideMenuInitialState: null,
    hideTopBar: false,
    custom: null,
    favIcon: null,
    hideActionsMenu: false,
    stickyTopNav: false,
    generalErrorContainerType: 'dialog',
    generalErrorMessage: 'Sorry, we encountered an error. Please wait a few minutes before you try again.',
    initialSideNavMode: new BehaviorSubject('over'),
    disableCloseSideNav: true,
    navBarLogoSrc: '',
    navBarHomeLink: '',
    showLogo: false,
    showAppName: false,
    appName: 'Sharegain',
    expanderPosition: 'top',
    isFloatingSideBar: false,
    showUserMenu: false,
    showNotifications: false,
  };

  static DEFAULT_BRAND_MAP: SgApplicationConfiguration['brandingMap'] = {
    noBrand: {
      logo: '',
      preLogo: '',
    },
  };

  name: string;
  authType: keyof SgAuthAdapterMap;
  branding: Required<SgAppBranding> & { name: string };
  brandType: 'subDomain' | 'query' | 'default' = 'default';
  beforeLogin: (environment: Environment) => void;

  constructor(private appConfig: SgApplicationConfiguration) {
    this.name = appConfig.name;
    this.authType = appConfig.authType;
    this.beforeLogin = typeof appConfig.beforeLogin !== 'function' ? noop : appConfig.beforeLogin;
    this.initBranding(appConfig);
  }

  static create(...appConfigs: SgApplicationConfiguration[]): SgAppConfig {
    // TODO:
    // Currently, matching is based on url (parsing).
    // In the future we might want this to be service based, role based sr subdomain based.
    // The entire process should be handled in matching\ and no change should be done here.
    // If it's async (service/role) then do the async operation before angular bootstraps - matching must
    // remain sync.
    for (const app of appConfigs) {
      let matcher = app.matcher;
      if (!matcher) {
        const re = new RegExp(`^(?:#\\/|#|\\/)(${app.name})(?:(?:\\/|\\?|\\().*)?$`);
        matcher = (location: Location) => re.test(location.pathname) || re.test(location.hash);
      }
      if (matcher(location)) {
        return new SgAppConfig(app);
      }
    }
  }

  static reEvaluateBranding(appConfig: SgAppConfig): void {
    appConfig.initBranding(appConfig.appConfig);
  }

  private initBranding(appConfig: SgApplicationConfiguration): void {
    this.branding = { ...SgAppConfig.DEFAULT_BRAND };
    const selectedBrand = getBrandingFromUrl();

    if (selectedBrand && applyBrandingFromMap(this.branding, selectedBrand.name, SgAppConfig.DEFAULT_BRAND_MAP)) {
      this.brandType = selectedBrand.type;
    }

    if (appConfig.branding) {
      Object.assign(this.branding, appConfig.branding);
    }
    if (
      selectedBrand &&
      appConfig.brandingMap &&
      applyBrandingFromMap(this.branding, selectedBrand.name, appConfig.brandingMap)
    ) {
      this.brandType = selectedBrand.type;
    }

    if (this.branding.logo) {
      this.branding.logo = getDeployUrl('/') + this.branding.logo;
    }

    if (this.branding.preLogo) {
      this.branding.preLogo = getDeployUrl('/') + this.branding.preLogo;
    } else {
      this.branding.preLogo = this.branding.logo;
    }
  }
}
